import { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, FileSpreadsheet, Wallet, CreditCard, Filter, Pencil, Users, FileText, History, Mail } from "lucide-react";
import { toast } from "sonner";
import PianiRatePage from "./PianiRatePage";

const METODI = ["Contanti", "Bonifico", "POS", "Altro"];
const STATI = [
  { v: "confermato", l: "Confermato", c: "bg-emerald-50 text-emerald-800" },
  { v: "in_attesa", l: "In attesa", c: "bg-amber-50 text-amber-900" },
  { v: "annullato", l: "Annullato", c: "bg-red-50 text-red-800" },
];
const statoBadge = (s) => STATI.find(x => x.v === s) || STATI[0];

function RegistroMensile() {
  const [classi, setClassi] = useState([]);
  const [studenti, setStudenti] = useState([]);
  const [pagamenti, setPagamenti] = useState([]);
  const [fClasse, setFClasse] = useState("tutte");
  const [fMese, setFMese] = useState("");
  const [fMetodo, setFMetodo] = useState("tutti");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const oggi = new Date().toISOString().slice(0, 10);
  const emptyForm = { classe_id: "", studente_id: "", mese: oggi.slice(0, 7), importo: "", metodo: "Contanti", data_pagamento: oggi, note: "", stato: "confermato" };
  const [form, setForm] = useState(emptyForm);
  const [editRow, setEditRow] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/classi"), api.get("/users?role=studente")])
      .then(([c, s]) => { setClassi(c.data); setStudenti(s.data); })
      .catch(() => toast.error("Errore caricamento dati"));
  }, []);

  const loadPagamenti = useCallback(async () => {
    const params = new URLSearchParams();
    if (fClasse !== "tutte") params.set("classe_id", fClasse);
    if (fMese) params.set("mese", fMese);
    if (fMetodo !== "tutti") params.set("metodo", fMetodo);
    const r = await api.get(`/registro-pagamenti?${params.toString()}`);
    setPagamenti(r.data);
  }, [fClasse, fMese, fMetodo]);
  useEffect(() => { loadPagamenti().catch(() => {}); }, [loadPagamenti]);

  const classeForm = classi.find(c => c.id === form.classe_id);
  const studentiForm = studenti.filter(s => classeForm?.studenti_ids?.includes(s.id));
  const nomeStud = (id) => { const u = studenti.find(s => s.id === id); return u ? `${u.name}${u.cognome ? " " + u.cognome : ""}` : id; };
  const nomeClasse = (id) => classi.find(c => c.id === id)?.nome || "-";

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/registro-pagamenti", { ...form, importo: +form.importo });
      toast.success("Pagamento registrato");
      setOpen(false);
      setForm(emptyForm);
      await loadPagamenti();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore registrazione"); }
    finally { setSaving(false); }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/registro-pagamenti/${editRow.id}`, { importo: +editRow.importo, mese: editRow.mese, metodo: editRow.metodo, data_pagamento: editRow.data_pagamento, note: editRow.note, stato: editRow.stato });
      toast.success("Pagamento aggiornato");
      setEditRow(null);
      await loadPagamenti();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore aggiornamento"); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare questa registrazione di pagamento?")) return;
    await api.delete(`/registro-pagamenti/${id}`);
    toast.success("Registrazione eliminata");
    setPagamenti(p => p.filter(x => x.id !== id));
  };
  const scaricaRicevuta = async (id) => {
    const token = localStorage.getItem("isteform_token");
    try {
      const r = await fetch(`${API}/registro-pagamenti/${id}/ricevuta`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!r.ok) { toast.error("Errore ricevuta"); return; }
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `ricevuta_${id}.pdf`;
      link.click();
      toast.success("Ricevuta scaricata");
    } catch { toast.error("Errore ricevuta"); }
  };
  const inviaRicevuta = async (id) => {
    if (!window.confirm("Inviare la ricevuta PDF via email allo studente?")) return;
    try {
      const { data } = await api.post(`/registro-pagamenti/${id}/invia-ricevuta`);
      toast.success(`Ricevuta inviata a ${data.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore invio ricevuta"); }
  };
  const exportExcel = async () => {
    const params = new URLSearchParams();
    if (fMetodo !== "tutti") params.set("metodo", fMetodo);
    const token = localStorage.getItem("isteform_token");
    try {
      const r = await fetch(`${API}/registro-pagamenti/export-excel?${params.toString()}`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!r.ok) { toast.error("Errore export"); return; }
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "registro_pagamenti.xlsx";
      link.click();
      toast.success("Excel scaricato: matrice studenti x mesi");
    } catch { toast.error("Errore export"); }
  };

  const totale = pagamenti.filter(p => p.stato !== "annullato").reduce((t, p) => t + (p.importo || 0), 0);
  const filtriAttivi = fClasse !== "tutte" || fMese || fMetodo !== "tutti";

  return (
    <div className="px-8 pb-8">
      <Card className="p-4 mb-6 bg-white border-[#E2E8F0]">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 text-sm text-stone-600"><Filter className="w-4 h-4 text-[#F7941E]"/> Filtri:</div>
          <div className="min-w-[220px]">
            <div className="text-xs text-stone-500 mb-1">Classe</div>
            <Select value={fClasse} onValueChange={setFClasse}>
              <SelectTrigger data-testid="registro-filtro-classe"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutte">Tutte le classi</SelectItem>
                {classi.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs text-stone-500 mb-1">Mese</div>
            <Input type="month" value={fMese} onChange={e => setFMese(e.target.value)} data-testid="registro-filtro-mese" className="w-40"/>
          </div>
          <div className="min-w-[160px]">
            <div className="text-xs text-stone-500 mb-1">Metodo</div>
            <Select value={fMetodo} onValueChange={setFMetodo}>
              <SelectTrigger data-testid="registro-filtro-metodo"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti i metodi</SelectItem>
                {METODI.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {filtriAttivi && (
            <button onClick={() => { setFClasse("tutte"); setFMese(""); setFMetodo("tutti"); }} className="text-sm text-[#F7941E] hover:underline" data-testid="registro-filtri-reset">Azzera filtri</button>
          )}
          <div className="w-full sm:w-auto sm:ml-auto flex flex-col sm:flex-row gap-2">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="new-pagamento-btn"><Plus className="w-4 h-4 mr-2"/>Registra Pagamento</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Registra pagamento mensile</DialogTitle><DialogDescription>Seleziona classe e studente: il pagamento viene associato al mese di riferimento.</DialogDescription></DialogHeader>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="col-span-2"><Label>Classe</Label>
                    <Select value={form.classe_id} onValueChange={v => setForm({ ...form, classe_id: v, studente_id: "" })}>
                      <SelectTrigger data-testid="pagamento-classe-select"><SelectValue placeholder="Seleziona classe"/></SelectTrigger>
                      <SelectContent>{classi.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>Studente</Label>
                    <Select value={form.studente_id} onValueChange={v => setForm({ ...form, studente_id: v })} disabled={!form.classe_id}>
                      <SelectTrigger data-testid="pagamento-studente-select"><SelectValue placeholder={form.classe_id ? "Seleziona studente" : "Prima seleziona la classe"}/></SelectTrigger>
                      <SelectContent>{studentiForm.map(s => <SelectItem key={s.id} value={s.id}>{s.name}{s.cognome ? ` ${s.cognome}` : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Mese di riferimento</Label><Input type="month" data-testid="pagamento-mese" value={form.mese} onChange={e => setForm({ ...form, mese: e.target.value })}/></div>
                  <div><Label>Data pagamento</Label><Input type="date" data-testid="pagamento-data" value={form.data_pagamento} onChange={e => setForm({ ...form, data_pagamento: e.target.value })}/></div>
                  <div><Label>Importo €</Label><Input type="number" step="0.01" data-testid="pagamento-importo" value={form.importo} onChange={e => setForm({ ...form, importo: e.target.value })}/></div>
                  <div><Label>Metodo</Label>
                    <Select value={form.metodo} onValueChange={v => setForm({ ...form, metodo: v })}>
                      <SelectTrigger data-testid="pagamento-metodo"><SelectValue/></SelectTrigger>
                      <SelectContent>{METODI.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>Note</Label><Input data-testid="pagamento-note" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}/></div>
                </div>
                <DialogFooter><Button onClick={save} disabled={saving} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="pagamento-save-btn">{saving ? "Salvataggio..." : "Registra"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Button variant="outline" onClick={exportExcel} className="border-emerald-700 text-emerald-700" data-testid="export-bilancio-btn"><FileSpreadsheet className="w-4 h-4 mr-2"/>Export Excel</Button>
          </div>
        </div>
      </Card>

      <Card className="p-4 bg-white border-[#E2E8F0]">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-stone-900">Pagamenti registrati ({pagamenti.length})</div>
          <div className="text-sm text-stone-600">Totale (esclusi annullati): <span className="font-mono font-bold text-[#F7941E]" data-testid="registro-totale">€ {totale.toFixed(2)}</span></div>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Data</TableHead><TableHead>Mese</TableHead><TableHead>Studente</TableHead><TableHead>Classe</TableHead>
            <TableHead>Importo</TableHead><TableHead>Metodo</TableHead><TableHead>Stato</TableHead><TableHead>Registrato da</TableHead><TableHead>Note</TableHead><TableHead className="text-right">Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {pagamenti.map(p => {
              const sb = statoBadge(p.stato);
              return (
              <TableRow key={p.id} data-testid={`pagamento-row-${p.id}`}>
                <TableCell>{p.data_pagamento}</TableCell>
                <TableCell className="font-mono">{p.mese}</TableCell>
                <TableCell className="font-medium">{nomeStud(p.studente_id)}</TableCell>
                <TableCell className="text-sm">{nomeClasse(p.classe_id)}</TableCell>
                <TableCell className="font-mono">€ {p.importo.toFixed(2)}</TableCell>
                <TableCell>{p.metodo}</TableCell>
                <TableCell><Badge className={sb.c} data-testid={`pagamento-stato-${p.id}`}>{sb.l}</Badge></TableCell>
                <TableCell className="text-xs text-stone-500">
                  {p.registrato_da_nome || "-"}
                  {p.modificato_at && (
                    <span className="inline-flex items-center gap-0.5 ml-1 text-[#F7941E] cursor-help" title={`Modificato da ${p.modificato_da_nome || "?"} il ${(p.modificato_at || "").slice(0, 16).replace("T", " ")}`} data-testid={`pagamento-modificato-${p.id}`}>
                      <History className="w-3 h-3"/>mod.
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-stone-500 text-sm">{p.note || "-"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => scaricaRicevuta(p.id)} title="Scarica ricevuta PDF" data-testid={`ricevuta-pagamento-${p.id}`}><FileText className="w-4 h-4 text-emerald-700"/></Button>
                  <Button variant="ghost" size="sm" onClick={() => inviaRicevuta(p.id)} title="Invia ricevuta via email" data-testid={`invia-ricevuta-${p.id}`}><Mail className="w-4 h-4 text-[#1F4E79]"/></Button>
                  <Button variant="ghost" size="sm" onClick={() => setEditRow({ ...p })} data-testid={`edit-pagamento-${p.id}`}><Pencil className="w-4 h-4 text-[#1F4E79]"/></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(p.id)} data-testid={`del-pagamento-${p.id}`}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                </TableCell>
              </TableRow>
            );})}
            {pagamenti.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-stone-500 py-8">Nessun pagamento per i filtri selezionati.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!editRow} onOpenChange={(o) => { if (!o) setEditRow(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Correggi pagamento</DialogTitle><DialogDescription>Modifica i dati di un pagamento inserito per errore.</DialogDescription></DialogHeader>
          {editRow && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Mese</Label><Input type="month" value={editRow.mese} onChange={e => setEditRow({ ...editRow, mese: e.target.value })} data-testid="edit-pagamento-mese"/></div>
              <div><Label>Data pagamento</Label><Input type="date" value={editRow.data_pagamento} onChange={e => setEditRow({ ...editRow, data_pagamento: e.target.value })} data-testid="edit-pagamento-data"/></div>
              <div><Label>Importo €</Label><Input type="number" step="0.01" value={editRow.importo} onChange={e => setEditRow({ ...editRow, importo: e.target.value })} data-testid="edit-pagamento-importo"/></div>
              <div><Label>Metodo</Label>
                <Select value={editRow.metodo} onValueChange={v => setEditRow({ ...editRow, metodo: v })}>
                  <SelectTrigger data-testid="edit-pagamento-metodo"><SelectValue/></SelectTrigger>
                  <SelectContent>{METODI.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Stato</Label>
                <Select value={editRow.stato || "confermato"} onValueChange={v => setEditRow({ ...editRow, stato: v })}>
                  <SelectTrigger data-testid="edit-pagamento-stato"><SelectValue/></SelectTrigger>
                  <SelectContent>{STATI.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Note</Label><Input value={editRow.note || ""} onChange={e => setEditRow({ ...editRow, note: e.target.value })} data-testid="edit-pagamento-note"/></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="edit-pagamento-save">Salva modifiche</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SituazioneAllievi() {
  const [classi, setClassi] = useState([]);
  const [fClasse, setFClasse] = useState("tutte");
  const [righe, setRighe] = useState([]);

  useEffect(() => { api.get("/classi").then(c => setClassi(c.data)).catch(() => {}); }, []);
  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (fClasse !== "tutte") params.set("classe_id", fClasse);
    const r = await api.get(`/situazione-allievi?${params.toString()}`);
    setRighe(r.data);
  }, [fClasse]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const totCifra = righe.reduce((t, r) => t + (r.cifra_concordata || 0), 0);
  const totVersato = righe.reduce((t, r) => t + (r.versato || 0), 0);
  const totResiduo = righe.reduce((t, r) => t + (r.residuo || 0), 0);

  return (
    <div className="px-8 pb-8">
      <Card className="p-4 mb-6 bg-white border-[#E2E8F0]">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 text-sm text-stone-600"><Filter className="w-4 h-4 text-[#F7941E]"/> Filtro:</div>
          <div className="min-w-[240px]">
            <div className="text-xs text-stone-500 mb-1">Classe</div>
            <Select value={fClasse} onValueChange={setFClasse}>
              <SelectTrigger data-testid="situazione-filtro-classe"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutte">Tutte le classi</SelectItem>
                {classi.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>
      <Card className="p-4 bg-white border-[#E2E8F0]">
        <div className="font-semibold text-stone-900 mb-3">Situazione allievi ({righe.length})</div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Allievo</TableHead><TableHead>Classe</TableHead>
            <TableHead className="text-right">Cifra Concordata</TableHead>
            <TableHead className="text-right">Versato</TableHead>
            <TableHead className="text-right">Residuo</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {righe.map(r => (
              <TableRow key={`${r.classe_id}-${r.studente_id}`} data-testid={`situazione-row-${r.studente_id}`}>
                <TableCell className="font-medium">{r.nome}</TableCell>
                <TableCell className="text-sm">{r.classe_nome}</TableCell>
                <TableCell className="text-right font-mono">€ {r.cifra_concordata.toFixed(2)}</TableCell>
                <TableCell className="text-right font-mono text-emerald-700">€ {r.versato.toFixed(2)}</TableCell>
                <TableCell className={`text-right font-mono font-bold ${r.residuo > 0 ? "text-red-600" : "text-stone-500"}`} data-testid={`situazione-residuo-${r.studente_id}`}>€ {r.residuo.toFixed(2)}</TableCell>
              </TableRow>
            ))}
            {righe.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-stone-500 py-8">Nessun allievo per il filtro selezionato.</TableCell></TableRow>}
          </TableBody>
          {righe.length > 0 && (
            <TableBody>
              <TableRow className="bg-[#0F2B48]/5 font-bold">
                <TableCell colSpan={2}>TOTALE</TableCell>
                <TableCell className="text-right font-mono">€ {totCifra.toFixed(2)}</TableCell>
                <TableCell className="text-right font-mono text-emerald-700">€ {totVersato.toFixed(2)}</TableCell>
                <TableCell className={`text-right font-mono ${totResiduo > 0 ? "text-red-600" : ""}`}>€ {totResiduo.toFixed(2)}</TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </Card>
    </div>
  );
}

export default function RegistroPagamentiPage() {
  const [tab, setTab] = useState("registro");
  return (
    <div>
      <div className="px-8 pt-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#F7941E] mb-2">Amministrazione</div>
        <h1 className="text-3xl font-bold text-stone-900 mb-1">Registrazione Pagamenti</h1>
        <p className="text-stone-600 mb-4">Registro per classe · Situazione allievi con cifra concordata · Piani rate integrati</p>
        <div className="flex gap-2 mb-6 flex-wrap">
          <Button variant={tab === "registro" ? "default" : "outline"} onClick={() => setTab("registro")}
            className={tab === "registro" ? "bg-[#F7941E] hover:bg-[#E08316] text-white" : ""} data-testid="tab-registro">
            <Wallet className="w-4 h-4 mr-2"/>Registro Mensile
          </Button>
          <Button variant={tab === "situazione" ? "default" : "outline"} onClick={() => setTab("situazione")}
            className={tab === "situazione" ? "bg-[#F7941E] hover:bg-[#E08316] text-white" : ""} data-testid="tab-situazione">
            <Users className="w-4 h-4 mr-2"/>Situazione Allievi
          </Button>
          <Button variant={tab === "piani" ? "default" : "outline"} onClick={() => setTab("piani")}
            className={tab === "piani" ? "bg-[#F7941E] hover:bg-[#E08316] text-white" : ""} data-testid="tab-piani-rate">
            <CreditCard className="w-4 h-4 mr-2"/>Piani Rate
          </Button>
        </div>
      </div>
      {tab === "registro" && <RegistroMensile/>}
      {tab === "situazione" && <SituazioneAllievi/>}
      {tab === "piani" && <PianiRatePage/>}
    </div>
  );
}
